import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Badge,
    Box,
    Button,
    Typography,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    TextField,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AgGridReact } from 'ag-grid-react';
import { CellClassParams, CellStyle, ColDef, ICellRendererParams } from 'ag-grid-community';
import type {
    CellDoubleClickedEvent,
    CellKeyDownEvent,
    CellValueChangedEvent,
} from 'ag-grid-community/dist/types/core/events';
import { BudgetSiSClassification, BudgetVersionType } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';
import { PendingConfigEdits } from '../../hooks/usePendingConfigEdits';
import {
    useCreateBillingConfig,
    useDeleteBillingConfig,
    useMissingBillingConfigsCount,
    useUpdateBillingConfig,
    useDownloadBillingConfigImportTemplate,
} from '../../hooks/api/budgetBillingConfig';
import { useWorkingUnitLocations } from '../../hooks/api/workingUnitLocations';
import { useHEMTLocations } from '../../hooks/api/useHEMTLocations';
import { useUpdateWorkingUnitClassification } from '../../hooks/api/budgetWorkingUnitClassification';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { fixed2ValueFormatter, numberValueFormatter } from '../../utils/numberUtils';
import BillingConfigImportDialog from './BillingConfigImportDialog';
import BucketManager from './BucketManager';
import CompleteMissingConfigsDialog from './CompleteMissingConfigsDialog';
import HintIcon from './HintIcon';
import ImportSplitButton from './ImportSplitButton';
import { parseBudgetCell, priceFormatter } from './helpers';
import { ORPHANED_ROW_BG, pendingBorder } from './pendingCellStyle';

interface BillingConfigGridProps {
    billingConfigs: BillingConfigWithRelations[];
    isLoading: boolean;
    /** The page's pending edits — cell edits are recorded here and saved by the page (CALL-779). */
    pending: PendingConfigEdits;
}

type WorkingUnitLocationOption = {
    id: number;
    workingUnitId: number | null;
    locationId: number | null;
    partnerName: string;
    locationName: string;
    worktypeId: number | null;
    worktypeName: string;
    channelId: number | null;
    channelName: string;
    taskId: number | null;
    taskName: string;
    workingUnitName: string;
    sisClassification: string | null;
};

type LocationOption = {
    locationId: number;
    label: string;
    partnerName: string;
    locationName: string;
};

type DialogMode = 'create' | 'edit' | null;

// The columns a cell edit may write — exactly the `fields` object of the batch payload (see
// updateBillingConfigsBulkSchema). A whitelist rather than "whatever the event carried", so a read-only
// column can never end up posted; it replaces the per-field if-chain the autosave handler used.
const EDITABLE_FIELDS = [
    'budgetBucketId',
    'costCenter',
    'prLabel',
    'serviceFactor',
    'defaultAbt',
    'defaultMinutePrice',
];

/** CALL-778: the checkbox columns. They are absent from EDITABLE_FIELDS because they never travel through
 *  onCellValueChanged — their own control records the edit (see booleanColumn / handleCellKeyDown). */
const BOOLEAN_FIELDS = ['flatFee', 'isHidden'];

/** CALL-780: the classification options for the inline editor, straight from the shared enum. */
const SIS_CLASSIFICATIONS = Object.values(BudgetSiSClassification);

const getWorkingUnitName = ({
    worktypeName,
    channelName,
    taskName,
}: {
    worktypeName: string;
    channelName: string;
    taskName: string;
}) => [worktypeName, channelName, taskName].filter(Boolean).join(' - ');

const BillingConfigGrid = ({ billingConfigs, isLoading, pending }: BillingConfigGridProps) => {
    const { t } = useTranslation('budget');
    // CALL-758: the selected bucket drives the grid. "All buckets" (undefined) lists every bucket's
    // configs for reassigning via the editable column; create/import then need a concrete bucket.
    const { canEdit, selectedBucketId, buckets, versions } = useBudgetContext();
    const gridRef = useRef<AgGridReact>(null);

    // CALL-758: a file import assigns configs to the selected bucket and seeds that bucket's draft
    // forecast — so it targets the bucket's active draft (one draft per bucket). If the bucket has
    // no draft, import is disabled (create a draft in this bucket first).
    const importVersionId = useMemo(
        () => versions.find((v) => v.type === BudgetVersionType.DRAFT && v.budgetBucketId === selectedBucketId)?.id,
        [versions, selectedBucketId]
    );
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showCompleteMissingDialog, setShowCompleteMissingDialog] = useState(false);
    // CALL-820: the badge count for "Complete missing entries" — fetched only when the button is on
    // screen (write users with a selected bucket); see the comment on the button for the semantics.
    const missingCount = useMissingBillingConfigsCount(canEdit ? (selectedBucketId ?? undefined) : undefined);
    const { mutate: downloadTemplate, isLoading: isDownloadingTemplate } = useDownloadBillingConfigImportTemplate();

    // Shared dialog state for create/edit
    const [dialogMode, setDialogMode] = useState<DialogMode>(null);
    const [dialogConfigId, setDialogConfigId] = useState<number | null>(null);
    const [dialogLocationId, setDialogLocationId] = useState<number | null>(null);
    const [dialogWulId, setDialogWulId] = useState<number | null>(null);
    const [dialogSisClassification, setDialogSisClassification] = useState<string | null>(null);
    const [dialogCostCenter, setDialogCostCenter] = useState('');
    const [dialogPrLabel, setDialogPrLabel] = useState('');
    const [dialogServiceFactor, setDialogServiceFactor] = useState('1');
    const [dialogFlatFee, setDialogFlatFee] = useState(false);
    const [dialogDefaultAbt, setDialogDefaultAbt] = useState('');
    const [dialogDefaultMinutePrice, setDialogDefaultMinutePrice] = useState('');

    const [workingUnitLocations] = useWorkingUnitLocations({ fetchAll: true });
    const hemtLocations = useHEMTLocations();

    // CALL-779: the single-config PATCH is now the create/edit DIALOG's path only — a cell edit goes
    // into the page's pending store and is written by its Save.
    const { mutateAsync: updateConfigAsync, isLoading: isUpdatingConfig } = useUpdateBillingConfig();
    const { mutateAsync: createConfigAsync, isLoading: isCreatingConfig } = useCreateBillingConfig({
        onSettled: () => {},
    });
    const { mutate: deleteConfig } = useDeleteBillingConfig();
    const {
        mutateAsync: updateClassificationAsync,
        mutate: updateClassification,
        isLoading: isUpdatingClassification,
    } = useUpdateWorkingUnitClassification();

    // CALL-775: synchronous re-entry guard covering the whole submit chain (classification → save).
    // The mutation isLoading flags only flip once each mutation starts, so the initial classification
    // await is otherwise unguarded; this ref blocks a second click from the first synchronous tick.
    const submittingRef = useRef(false);

    const locationMap = useMemo(
        () => new Map(hemtLocations.map((location) => [location.id, location])),
        [hemtLocations]
    );

    const workingUnitLocationOptions = useMemo<WorkingUnitLocationOption[]>(
        () =>
            workingUnitLocations
                .map((wul) => {
                    const locationId = wul.worktypeLocation?.locationId ?? null;
                    const location = locationId ? locationMap.get(locationId) : undefined;
                    const worktypeName = wul.workingUnit?.worktype?.worktype ?? '';
                    const channelName = wul.workingUnit?.channel?.name ?? '';
                    const taskName = wul.workingUnit?.task?.name ?? '';

                    return {
                        id: wul.id,
                        workingUnitId: wul.workingUnit?.id ?? null,
                        locationId,
                        partnerName: location?.code ?? '',
                        locationName: location?.city ?? '',
                        worktypeId: wul.workingUnit?.worktypeId ?? null,
                        worktypeName,
                        channelId: wul.workingUnit?.channelId ?? null,
                        channelName,
                        taskId: wul.workingUnit?.taskId ?? null,
                        taskName,
                        workingUnitName: getWorkingUnitName({ worktypeName, channelName, taskName }),
                        sisClassification: wul.workingUnit?.budgetSiSClassification ?? null,
                    };
                })
                .sort((left, right) =>
                    [
                        left.partnerName,
                        left.locationName,
                        left.worktypeName,
                        left.channelName,
                        left.taskName,
                        String(left.id),
                    ]
                        .join('|')
                        .localeCompare(
                            [
                                right.partnerName,
                                right.locationName,
                                right.worktypeName,
                                right.channelName,
                                right.taskName,
                                String(right.id),
                            ].join('|')
                        )
                ),
        [locationMap, workingUnitLocations]
    );

    const locationOptions = useMemo<LocationOption[]>(
        () =>
            hemtLocations
                .map((loc) => ({
                    locationId: loc.id,
                    label: `${loc.code} - ${loc.city}`,
                    partnerName: loc.code,
                    locationName: loc.city,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [hemtLocations]
    );

    const dialogWorkingUnitOptions = useMemo(
        () =>
            dialogLocationId != null ? workingUnitLocationOptions.filter((o) => o.locationId === dialogLocationId) : [],
        [workingUnitLocationOptions, dialogLocationId]
    );

    const servedById = useMemo(() => new Map(billingConfigs.map((config) => [config.id, config])), [billingConfigs]);

    // This grid's slice of the pending set: config id -> field -> value.
    const pendingByConfig = useMemo(() => {
        const byConfig = new Map<number, Map<string, unknown>>();

        for (const edit of pending.edits) {
            if (edit.kind !== 'field') continue;
            const fields = byConfig.get(edit.rowId) ?? new Map<string, unknown>();
            fields.set(edit.key, edit.value);
            byConfig.set(edit.rowId, fields);
        }

        return byConfig;
    }, [pending.edits]);

    // CALL-780: what this bucket already contains, as partner / location / worktype triples. The dialog
    // preselects the proposals matching them, so a user filling gaps in a populated bucket does not have
    // to pick rows out of a list of hundreds. Read from the served configs rather than from a new
    // endpoint: this grid is already scoped to the selected bucket whenever the action is available.
    const bucketSignatures = useMemo(
        () =>
            billingConfigs.map((config) => ({
                partnerName: config.partnerName ?? '',
                locationName: config.locationName ?? '',
                worktypeName: config.workingUnitLocation?.workingUnit?.worktype?.worktype ?? '',
            })),
        [billingConfigs]
    );

    // CALL-780: an unsaved bucket reassignment blocks "Complete missing entries". The detection endpoint
    // reads server truth, so a row pending a move INTO the selected bucket still counts as belonging to
    // its old one and its combination is offered here — creating it would then make the later Save
    // collide on (budgetBucketId, workingUnitLocationId), and that Save is one transaction, so the one
    // bad row would fail the whole page's edits. Every other pending field is unaffected.
    const hasPendingBucketMove = useMemo(
        () => pending.edits.some((edit) => edit.kind === 'field' && edit.key === 'budgetBucketId'),
        [pending.edits]
    );

    // CALL-820: whether the loaded list carries any orphaned row — gates the status column below,
    // so a healthy environment sees the grid exactly as before (no dead leading column).
    const hasOrphans = useMemo(
        () => billingConfigs.some((config) => config.hasSourceVolume === false),
        [billingConfigs]
    );

    const rowData = useMemo(
        () =>
            billingConfigs.map((config) => {
                const worktypeName = config.workingUnitLocation?.workingUnit?.worktype?.worktype ?? '';
                const channelName = config.workingUnitLocation?.workingUnit?.channel?.name ?? '';
                const taskName = config.workingUnitLocation?.workingUnit?.task?.name ?? '';

                return {
                    ...config,
                    partnerName: config.partnerName ?? '',
                    locationName: config.locationName ?? '',
                    worktypeName,
                    channelName,
                    taskName,
                    workingUnitName:
                        config.workingUnitName || getWorkingUnitName({ worktypeName, channelName, taskName }),
                    // CALL-780: the classification is written on the Working Unit, not on this config, so
                    // the inline editor needs its id.
                    workingUnitId: config.workingUnitLocation?.workingUnit?.id ?? null,
                    // CALL-779: pending edits sit on top of the served row, so an unsaved cell survives the
                    // refetch that follows any other write on the page (ag-grid's own copy of the row does
                    // not — new rowData replaces it).
                    ...Object.fromEntries(pendingByConfig.get(config.id) ?? []),
                };
            }),
        [billingConfigs, pendingByConfig]
    );

    // CALL-779: an edit is recorded, not saved. The per-field normalisation this used to do is gone with
    // it: `serviceFactor` and `defaultAbt` now declare a valueParser, so the value arriving here is
    // already a number or null — parsed as German, which `parseFloat`/`parseInt` never did (a pasted
    // "1,5" read as 1 and an unparseable one was silently rewritten to 1). What the old chain refused is
    // now refused visibly, by the save bar (see firstConfigEditIssue).
    const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef } = event;

            // CALL-780: the classification is NOT a config column — it lives on the Working Unit, the batch
            // PATCH's schema strips it, and it is shared by every config using that unit. So it is written
            // straight away by its own endpoint rather than joining the page's pending set.
            if (colDef.field === 'sisClassification') {
                if (data?.workingUnitId && data.sisClassification) {
                    // `mutate`, not `mutateAsync`: nothing here awaits the result, and the hook's onError
                    // already raises the toast while its invalidation restores the served value. Same
                    // fire-and-forget shape as the row delete.
                    updateClassification({
                        id: Number(data.workingUnitId),
                        budgetSiSClassification: data.sisClassification as BudgetSiSClassification,
                    });
                }
                return;
            }

            if (!data?.id || !colDef.field || !EDITABLE_FIELDS.includes(colDef.field)) return;

            const configId = Number(data.id);
            const served = servedById.get(configId) as Record<string, unknown> | undefined;
            pending.set(configId, 'field', colDef.field, data[colDef.field], served?.[colDef.field]);
        },
        [pending, servedById, updateClassification]
    );

    // ag-grid does not re-run cellStyle for a cell whose value did not change, so discarding — where the
    // border must go while the value returns to what it already was — needs a forced repaint. Same
    // behaviour CALL-752 documented in useRateGrid.
    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [rowData, pendingByConfig]);

    // A red border marks an unsaved cell, over whatever background the column already had — and a clean
    // cell says so explicitly, or ag-grid's additive styling leaves the border painted after a save.
    const modifiedStyle = useCallback(
        (params: CellClassParams): CellStyle => {
            const field = params.colDef?.field;
            return pendingBorder(
                Boolean(field && params.data?.id != null && pendingByConfig.get(Number(params.data.id))?.has(field))
            );
        },
        [pendingByConfig]
    );

    // The yellow input background that marks a column as editable, over the pending-edit border. The
    // Bucket column carried it alone, so every other editable column looked read-only — including the
    // ones a completed row is meant to be filled in afterwards (cost center, PR, service factor,
    // default ABT, flat fee, hidden).
    const editableStyle = useCallback(
        (params: CellClassParams): CellStyle => ({
            ...(canEdit ? { backgroundColor: '#fffde7' } : {}),
            ...modifiedStyle(params),
        }),
        [canEdit, modifiedStyle]
    );

    /** Flip one boolean cell, measured against what the server last served. The single write path behind
     *  both the checkbox and the keyboard shortcut. */
    const toggleBoolean = useCallback(
        (configId: number, field: string, next: boolean) => {
            const served = servedById.get(configId) as Record<string, unknown> | undefined;
            pending.set(configId, 'field', field, next, served?.[field]);
        },
        [pending, servedById]
    );

    /** CALL-778: the boolean columns are not ag-grid-`editable` (their checkbox is the control), so
     *  ag-grid's own Enter-to-edit never reaches them — Space/Enter on the focused cell has to do it, or
     *  the column becomes mouse-only. The rich-select editor this replaced was keyboard-operable, and
     *  losing that is not an acceptable trade for a nicer click. */
    const handleCellKeyDown = useCallback(
        (event: CellKeyDownEvent) => {
            const field = event.colDef?.field;
            if (!canEdit || !field || !BOOLEAN_FIELDS.includes(field)) return;

            const key = (event.event as KeyboardEvent | null)?.key;
            if (key !== ' ' && key !== 'Enter') return;

            const configId = Number(event.data?.id);
            if (!configId) return;

            // Space would scroll the grid otherwise.
            event.event?.preventDefault();
            // event.data is the row AFTER the pending overlay, so this reads the effective value the user
            // sees — the same thing the checkbox renders.
            toggleBoolean(configId, field, !event.data?.[field]);
        },
        [canEdit, toggleBoolean]
    );

    /** A boolean column: one click on the checkbox flips it.
     *
     *  CALL-778: both booleans used an agRichSelectCellEditor over [true, false]. A two-option dropdown
     *  is the wrong control for one bit — it costs a double-click plus a pick, and the open list
     *  highlights the CURRENT value, which reads as though the selection had landed on the wrong option.
     *
     *  The checkbox records the pending edit itself instead of going through onCellValueChanged: the
     *  column is deliberately not `editable`, so ag-grid never opens an editor over it (a plain
     *  `editable` boolean column would get the default TEXT editor on double-click). `rowData` overlays
     *  the pending value, so the box flips at once and picks up the red unsaved border like any cell. */
    const booleanColumn = useCallback(
        (field: string, headerName: string, minWidth: number): ColDef => ({
            headerName,
            field,
            minWidth,
            cellStyle: editableStyle,
            editable: false,
            cellRenderer: (params: ICellRendererParams) => {
                if (!params.data) return null;
                const configId = Number(params.data.id);
                return (
                    <Checkbox
                        checked={!!params.data[field]}
                        disabled={!canEdit}
                        style={{ padding: 0 }}
                        inputProps={{ 'aria-label': headerName }}
                        onChange={(event) => toggleBoolean(configId, field, event.target.checked)}
                    />
                );
            },
        }),
        [canEdit, editableStyle, toggleBoolean]
    );

    const resetDialog = useCallback(() => {
        setDialogMode(null);
        setDialogConfigId(null);
        setDialogLocationId(null);
        setDialogWulId(null);
        setDialogSisClassification(null);
        setDialogCostCenter('');
        setDialogPrLabel('');
        setDialogServiceFactor('1');
        setDialogFlatFee(false);
        setDialogDefaultAbt('');
        setDialogDefaultMinutePrice('');
    }, []);

    const openCreateDialog = useCallback(() => {
        resetDialog();
        setDialogMode('create');
    }, [resetDialog]);

    const openEditDialog = useCallback(
        (id: number) => {
            const served = billingConfigs.find((c) => c.id === id);
            if (!served) return;
            // CALL-779: seeded from the EFFECTIVE row — the served values with this row's pending edits on
            // top. Reading the served row alone put the PRE-edit value in the form, so saving the dialog
            // wrote the old one back and the still-pending edit re-overlaid it on the fresh server row.
            const config = {
                ...served,
                ...Object.fromEntries(pendingByConfig.get(id) ?? []),
            } as typeof served;
            const locationId = config.locationId ?? config.workingUnitLocation?.worktypeLocation?.locationId ?? null;
            setDialogMode('edit');
            setDialogConfigId(id);
            setDialogLocationId(locationId);
            setDialogWulId(config.workingUnitLocationId);
            setDialogSisClassification(config.sisClassification ?? null);
            setDialogCostCenter(config.costCenter ?? '');
            setDialogPrLabel(config.prLabel ?? '');
            setDialogServiceFactor(String(config.serviceFactor ?? 1));
            setDialogFlatFee(config.flatFee ?? false);
            setDialogDefaultAbt(config.defaultAbt != null ? String(config.defaultAbt) : '');
            // German notation in, German notation out (parseBudgetCell reads "0.5" as 5, so a dotted String()
            // round-trip would multiply the price by ten on the next save).
            setDialogDefaultMinutePrice(
                config.defaultMinutePrice != null
                    ? Number(config.defaultMinutePrice).toLocaleString('de-DE', { maximumFractionDigits: 4 })
                    : ''
            );
        },
        [billingConfigs, pendingByConfig]
    );

    const serviceFactorError = useMemo(() => {
        const trimmed = dialogServiceFactor.trim();
        if (trimmed === '') return null;
        const parsed = Number(trimmed);
        if (Number.isNaN(parsed) || parsed < 0) return t('validation.serviceFactorNegative');
        return null;
    }, [dialogServiceFactor, t]);

    const defaultAbtError = useMemo(() => {
        const trimmed = dialogDefaultAbt.trim();
        if (trimmed === '') return null;
        const parsed = Number(trimmed);
        if (!Number.isInteger(parsed) || parsed <= 0) return t('validation.defaultAbtPositiveInteger');
        return null;
    }, [dialogDefaultAbt, t]);

    // CALL-824: any non-negative decimal; empty = no fallback, 0 = a real zero price.
    const defaultMinutePriceError = useMemo(() => {
        const trimmed = dialogDefaultMinutePrice.trim();
        if (trimmed === '') return null;
        // A text field parsed like the grid cell (German-pinned): type="number" would turn "0,6" into an
        // empty value on a non-German browser and the save would silently NULL the default.
        const parsed = parseBudgetCell(trimmed);
        if (parsed === null || parsed < 0) return t('validation.defaultMinutePriceNonNegative');
        return null;
    }, [dialogDefaultMinutePrice, t]);

    const costCenterError = useMemo(() => {
        if (dialogCostCenter.trim() === '') return t('validation.costCenterRequired');
        return null;
    }, [dialogCostCenter, t]);

    const dialogHasErrors = Boolean(
        serviceFactorError || defaultAbtError || defaultMinutePriceError || costCenterError
    );

    const handleDialogConfirm = useCallback(async () => {
        if (submittingRef.current) return;
        if (!dialogWulId) return;
        if (dialogHasErrors) return;

        submittingRef.current = true;
        try {
            const selected = workingUnitLocationOptions.find((o) => o.id === dialogWulId);
            if (dialogSisClassification) {
                if (selected?.workingUnitId && !selected.sisClassification) {
                    await updateClassificationAsync({
                        id: selected.workingUnitId,
                        budgetSiSClassification: dialogSisClassification as BudgetSiSClassification,
                    });
                }
            }

            const payload = {
                workingUnitLocationId: dialogWulId,
                // CALL-744 #5: send explicit null (not undefined) for cleared optional fields so the
                // PATCH actually nulls them — an undefined value is dropped from the JSON body and the
                // backend treats the absent key as "no change", making the field impossible to clear.
                costCenter: dialogCostCenter || null,
                prLabel: dialogPrLabel || null,
                serviceFactor: Number.isNaN(parseFloat(dialogServiceFactor)) ? 1 : parseFloat(dialogServiceFactor),
                flatFee: dialogFlatFee,
                defaultAbt: dialogDefaultAbt ? parseInt(dialogDefaultAbt, 10) : null,
                defaultMinutePrice:
                    dialogDefaultMinutePrice.trim() !== '' ? parseBudgetCell(dialogDefaultMinutePrice) : null,
            };

            if (dialogMode === 'create') {
                // CALL-758: a new config row is created in the selected bucket (Add is disabled in the
                // "All buckets" view, so a concrete bucket is always chosen here).
                if (selectedBucketId == null) return;
                // CALL-780: the bucket's draft rides along so the new row's ABT and forecast are seeded
                // from Strategic Volume, exactly as the bulk action and the file import do.
                await createConfigAsync({ ...payload, budgetBucketId: selectedBucketId, versionId: importVersionId });
            } else if (dialogMode === 'edit' && dialogConfigId) {
                await updateConfigAsync({ id: dialogConfigId, ...payload } as { id: number });
                // CALL-779: the columns the dialog just wrote are spent — left pending, the next Save
                // would post them back over the dialog's values.
                //
                // CALL-778: scoped to the payload's OWN keys, not every `field` edit of the row. The
                // dialog does not own all editable columns — budgetBucketId (CALL-758) and isHidden are
                // grid-only — so a blanket drop discarded those pending edits without ever writing them:
                // tick Ausgeblendet, open and confirm the dialog, and the tick was gone with the DB still
                // holding the old value. Derived from `payload` so the two can never drift apart again.
                pending.dropCells(dialogConfigId, 'field', Object.keys(payload));
            }

            resetDialog();
        } finally {
            submittingRef.current = false;
        }
    }, [
        dialogHasErrors,
        dialogMode,
        dialogConfigId,
        dialogWulId,
        dialogCostCenter,
        dialogPrLabel,
        dialogServiceFactor,
        dialogFlatFee,
        dialogDefaultAbt,
        dialogDefaultMinutePrice,
        dialogSisClassification,
        selectedBucketId,
        importVersionId,
        workingUnitLocationOptions,
        updateClassificationAsync,
        createConfigAsync,
        updateConfigAsync,
        resetDialog,
        pending,
    ]);

    // Auto-select when only one working unit option available
    useEffect(() => {
        if (dialogWorkingUnitOptions.length === 1 && dialogWulId !== dialogWorkingUnitOptions[0].id) {
            setDialogWulId(dialogWorkingUnitOptions[0].id);
            setDialogSisClassification(dialogWorkingUnitOptions[0].sisClassification);
        }
    }, [dialogWorkingUnitOptions, dialogWulId]);

    const handleDeleteRow = useCallback((id: number) => {
        setDeleteConfirmId(id);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        if (deleteConfirmId !== null) {
            // CALL-779: forget the row's pending cells — a later Save must not try to write a config
            // that no longer exists. Waits for the delete to LAND: dropping them beside the
            // fire-and-forget mutation lost the edits for good whenever the DELETE then failed and the
            // row came back.
            deleteConfig(deleteConfirmId, { onSuccess: () => pending.dropRow(deleteConfirmId) });
            setDeleteConfirmId(null);
        }
    }, [deleteConfig, deleteConfirmId, pending]);

    // CALL-820: the orphaned row's remaining (white) cells get a pale red wash so the row reads as
    // one flagged unit, not a lone icon. Cells with their own background (editable yellow) keep it —
    // cell styles win over the row style. MUI red 50: the error family of the LinkOff icon, well
    // apart from the editable cream and the gap-orange. The healthy branch resets EXPLICITLY: ag-grid
    // reuses row elements (getRowId), so an omitted key would leave the wash on whatever row inherits
    // the element — the same trap pendingCellStyle documents for cells.
    const getRowStyle = useCallback(
        (params: { data?: { hasSourceVolume?: boolean } }) =>
            params.data?.hasSourceVolume === false ? { backgroundColor: ORPHANED_ROW_BG } : { backgroundColor: '' },
        []
    );

    // CALL-820 UX: double-clicking anywhere on the row that is NOT an inline-editable cell opens the
    // edit dialog — the same second entry the Overview grid gives its detail dialog. Editable cells
    // keep their double-click for the inline editor, and the actions column keeps its buttons.
    const handleCellDoubleClicked = useCallback(
        (event: CellDoubleClickedEvent) => {
            if (!canEdit || !event.data?.id) return;
            const field = event.colDef?.field ?? '';
            if (
                EDITABLE_FIELDS.includes(field) ||
                BOOLEAN_FIELDS.includes(field) ||
                field === 'sisClassification' ||
                field === 'actions'
            ) {
                return;
            }
            openEditDialog(Number(event.data.id));
        },
        [canEdit, openEditDialog]
    );

    const columnDefs: ColDef[] = useMemo(
        () => [
            // CALL-820: an orphaned row (working unit without active strategic volumes) is marked
            // where it gets FIXED — this page is where it is hidden or replaced via "Complete
            // missing entries". Its own slim leading column, like the Overview's status icon (a
            // state marker inside a text cell reads as part of the value) — and only rendered while
            // an orphan exists, so the healthy grid keeps its old shape.
            ...(hasOrphans
                ? [
                      {
                          colId: 'orphanStatus',
                          headerName: '',
                          headerTooltip: t('field.status'),
                          field: 'hasSourceVolume',
                          width: 44,
                          minWidth: 44,
                          maxWidth: 44,
                          editable: false,
                          sortable: false,
                          filter: false,
                          resizable: false,
                          // Icon-only cell: flex-centre both axes (see the Overview status column).
                          cellStyle: {
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                          } as CellStyle,
                          cellRenderer: (params: ICellRendererParams) =>
                              params.data?.hasSourceVolume === false ? (
                                  <Tooltip title={t('hint.orphanedWorkingUnit')} arrow>
                                      <LinkOffIcon fontSize="small" color="error" sx={{ verticalAlign: 'middle' }} />
                                  </Tooltip>
                              ) : null,
                      } as ColDef,
                  ]
                : []),
            {
                // CALL-758: bucket first — the row's silo, editable to reassign it to another bucket.
                headerName: t('field.bucket'),
                field: 'budgetBucketId',
                minWidth: 150,
                editable: canEdit,
                cellStyle: editableStyle,
                valueFormatter: (params) => buckets.find((b) => b.id === params.value)?.name ?? '',
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: buckets.map((b) => b.id),
                    formatValue: (value: number) => buckets.find((b) => b.id === value)?.name ?? '',
                },
            },
            {
                headerName: t('field.partner'),
                field: 'partnerName',
                minWidth: 140,
                editable: false,
            },
            {
                headerName: t('field.location'),
                field: 'locationName',
                minWidth: 150,
                editable: false,
            },
            {
                headerName: t('field.worktype'),
                field: 'worktypeName',
                minWidth: 180,
                editable: false,
            },
            {
                headerName: t('field.channel'),
                field: 'channelName',
                minWidth: 140,
                editable: false,
            },
            {
                headerName: t('field.task'),
                field: 'taskName',
                minWidth: 150,
                editable: false,
            },
            {
                headerName: t('field.classification'),
                field: 'sisClassification',
                minWidth: 130,
                // CALL-780: fillable only while EMPTY. The value lives on the Working Unit and is therefore
                // shared by every config using that unit, so overwriting one from here would silently
                // reclassify rows the user is not looking at — the create dialog already refuses to touch an
                // existing one for the same reason. Filling a blank is what a freshly completed row needs.
                editable: (params) => canEdit && !params.data?.sisClassification,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: { values: SIS_CLASSIFICATIONS },
                cellStyle: (params: CellClassParams): CellStyle =>
                    canEdit && !params.data?.sisClassification ? { backgroundColor: '#fffde7' } : {},
            },
            {
                headerName: t('field.costCenter'),
                field: 'costCenter',
                minWidth: 130,
                editable: canEdit,
                cellStyle: editableStyle,
            },
            {
                headerName: t('field.prName'),
                field: 'prLabel',
                minWidth: 220,
                editable: canEdit,
                cellStyle: editableStyle,
            },
            {
                headerName: t('field.serviceFactor'),
                field: 'serviceFactor',
                minWidth: 120,
                editable: canEdit,
                cellStyle: editableStyle,
                // CALL-796: German decimals like the rest of the grid — this was the one budget column
                // rendering "1.50" next to columns showing "1.000,00 €".
                valueFormatter: fixed2ValueFormatter,
                // CALL-794/779: both numeric columns declare a parser, or a value pasted from Excel is
                // stored verbatim — this column had none, and the handler's parseFloat read a German
                // "1,5" as 1 and "1.000" as 1. parseBudgetCell pins the locale (see helpers.ts).
                valueParser: (params) => parseBudgetCell(params.newValue),
            },
            {
                headerName: t('field.defaultAbt'),
                field: 'defaultAbt',
                minWidth: 120,
                editable: canEdit,
                cellStyle: editableStyle,
                // Deliberately NOT rounded — not even in display: the save bar refuses a fractional
                // Standard-ABT by name (validation.defaultAbtPositiveInteger) rather than quietly
                // turning 5,7 into 6, so the formatter has to keep that 5,7 on screen.
                valueFormatter: numberValueFormatter,
                valueParser: (params) => parseBudgetCell(params.newValue),
            },
            {
                // CALL-824: fallback €/minute, read after the month price and its projection.
                headerName: t('field.defaultMinutePrice'),
                field: 'defaultMinutePrice',
                minWidth: 140,
                editable: canEdit,
                cellStyle: editableStyle,
                valueFormatter: (params) =>
                    params.value == null || params.value === '' ? '' : priceFormatter.format(Number(params.value)),
                valueParser: (params) => parseBudgetCell(params.newValue),
                // Seed the editor from the FORMATTED value: the raw "1.234" of a stored 1.234 would re-parse
                // German as 1234 on a no-op Enter. parseBudgetCell strips the € the formatter adds.
                cellEditorParams: { useFormatter: true },
            },
            booleanColumn('flatFee', t('field.flatFee'), 100),
            // CALL-778: display-only visibility flag, global for all users. This grid deliberately keeps
            // listing hidden rows — it is the only place the flag can be cleared again. Only the Overview
            // detail grid drops them, and never from its totals.
            booleanColumn('isHidden', t('field.isHidden'), 110),
            ...(canEdit
                ? [
                      {
                          headerName: '',
                          field: 'actions',
                          minWidth: 90,
                          maxWidth: 90,
                          sortable: false,
                          filter: false,
                          editable: false,
                          cellRenderer: (params: ICellRendererParams) => {
                              if (!params.data) return null;
                              return (
                                  <>
                                      <IconButton size="small" onClick={() => openEditDialog(params.data.id)}>
                                          <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleDeleteRow(params.data.id)}>
                                          <DeleteIcon fontSize="small" />
                                      </IconButton>
                                  </>
                              );
                          },
                      },
                  ]
                : []),
        ],
        [openEditDialog, handleDeleteRow, canEdit, buckets, t, editableStyle, booleanColumn, hasOrphans]
    );

    const defaultColDef: ColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
        }),
        []
    );

    return (
        <Box className="budget-grid">
            {/* CALL-758: bucket selector + management sit at the very top — the bucket is the page's
                primary scope control (which silo's configs are shown / created / imported). */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BucketManager />
                {/* CALL-780: the whole action sits in the BUCKET row, not in the grid toolbar. It creates
                    rows in the selected bucket, so this is its natural level — and in German a third
                    button there wrapped the toolbar onto a second line at 1200px.

                    CALL-820 revisits CALL-780's "no count" decision: the count is now served by a
                    DB-only endpoint mode (no HEMT round-trip), so a page load can afford it — and the
                    Kairo working units sat unnoticed in this dialog for weeks precisely because
                    nothing said it had rows. The number can still exceed the dialog's list (it keeps
                    combinations whose location the dialog cannot resolve), so the tooltip calls it
                    "up to". The span keeps the tooltip reachable — MUI drops it on a disabled button. */}
                {canEdit && (
                    <Tooltip
                        title={
                            hasPendingBucketMove
                                ? t('hint.saveBucketMovesFirst')
                                : missingCount > 0
                                  ? t('hint.missingCountBadge', { count: missingCount })
                                  : ''
                        }
                    >
                        <span style={{ marginLeft: 'auto' }}>
                            <Badge badgeContent={missingCount} color="warning" max={99}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PlaylistAddIcon />}
                                    onClick={() => setShowCompleteMissingDialog(true)}
                                    // isLoading matters as much as the bucket: useGlobalBillingConfigs keeps the
                                    // PREVIOUS bucket's rows while the new ones load (keepPreviousData), so
                                    // opening the dialog in that window would build bucketSignatures from the
                                    // bucket the user just left and preselect the wrong proposals.
                                    disabled={selectedBucketId == null || hasPendingBucketMove || isLoading}
                                >
                                    <span>{t('billingConfig.completeMissing')}</span>
                                </Button>
                            </Badge>
                        </span>
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        <span>{t('billingConfig.title')}</span>
                    </Typography>
                    {/* CALL-780: behind the `?` rather than in the page flow, like the Prices & ABT sections
                        — it is a one-off orientation note, and it was pushing the grid down on every visit. */}
                    <HintIcon notes={t('hint.billingConfigEntries')} />
                </Box>
                {canEdit && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <ImportSplitButton
                            onImport={() => setShowImportDialog(true)}
                            // CALL-758: import targets the selected bucket's draft (it seeds that
                            // draft's forecast). Disabled when the bucket has no draft. The template
                            // menu stays enabled regardless — templates are version-agnostic.
                            importDisabled={importVersionId == null}
                            templatesDisabled={isDownloadingTemplate}
                            templateOptions={[
                                {
                                    label: t('import.downloadTemplateWithValues'),
                                    onClick: () => downloadTemplate('full'),
                                },
                                {
                                    label: t('import.downloadSimpleTemplate'),
                                    onClick: () => downloadTemplate('simple'),
                                },
                            ]}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={openCreateDialog}
                            disabled={workingUnitLocationOptions.length === 0 || selectedBucketId == null}
                        >
                            <span>{t('action.addRecord')}</span>
                        </Button>
                    </Box>
                )}
            </Box>
            <TableGridItem
                ref={gridRef}
                height="auto"
                domLayout="autoHeight"
                rowData={rowData}
                getRowStyle={getRowStyle}
                onCellDoubleClicked={handleCellDoubleClicked}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onCellValueChanged={handleCellValueChanged}
                onCellKeyDown={handleCellKeyDown}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                cellSelection
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('hint.noBillingConfigsCreate')}</span>}
            />
            <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="sm">
                <DialogTitle>{t('billingConfig.delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('dialog.deleteBillingConfig')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmId(null)} variant="outlined">
                        {t('action.cancel')}
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        {t('action.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ResponsiveDialog
                open={dialogMode !== null}
                onClose={resetDialog}
                title={t(dialogMode === 'edit' ? 'billingConfig.edit' : 'billingConfig.add')}
                isConfirmationDialog
                onConfirm={handleDialogConfirm}
                confirmButtonText={t(dialogMode === 'edit' ? 'action.save' : 'action.create')}
                // CALL-775: disable Save for the whole submit chain (classification → create/update)
                // so a double-click can't fire two concurrent requests that race on the config's
                // unique indexes. A synchronous ref guard in handleDialogConfirm covers the same-tick gap.
                isLoading={isUpdatingConfig || isCreatingConfig || isUpdatingClassification}
                disableConfirmButton={!dialogWulId || dialogHasErrors}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 400 }}>
                    <Autocomplete
                        options={locationOptions}
                        getOptionLabel={(option) => option.label}
                        value={locationOptions.find((o) => o.locationId === dialogLocationId) ?? null}
                        onChange={(_, value) => {
                            setDialogLocationId(value?.locationId ?? null);
                            setDialogWulId(null);
                            setDialogSisClassification(null);
                        }}
                        isOptionEqualToValue={(option, value) => option.locationId === value.locationId}
                        renderInput={(params) => (
                            <TextField {...params} label={t('field.location')} size="small" required />
                        )}
                    />
                    <Autocomplete
                        options={dialogWorkingUnitOptions}
                        getOptionLabel={(option) => option.workingUnitName}
                        value={dialogWorkingUnitOptions.find((o) => o.id === dialogWulId) ?? null}
                        onChange={(_, value) => {
                            setDialogWulId(value?.id ?? null);
                            setDialogSisClassification(value?.sisClassification ?? null);
                        }}
                        disabled={dialogLocationId == null}
                        noOptionsText={dialogLocationId == null ? t('hint.selectLocationFirst') : undefined}
                        renderInput={(params) => (
                            <TextField {...params} label={t('field.workingUnit')} size="small" required />
                        )}
                    />
                    <Autocomplete
                        options={[
                            BudgetSiSClassification.SERVICE,
                            BudgetSiSClassification.SIS,
                            BudgetSiSClassification.SALES,
                            BudgetSiSClassification.RETAIN,
                        ]}
                        value={dialogSisClassification}
                        onChange={(_, value) => setDialogSisClassification(value)}
                        disabled={
                            !dialogWulId ||
                            !!workingUnitLocationOptions.find((o) => o.id === dialogWulId)?.sisClassification
                        }
                        renderInput={(params) => (
                            <TextField {...params} label={t('field.classification')} size="small" />
                        )}
                    />
                    <TextField
                        label={t('field.costCenter')}
                        value={dialogCostCenter}
                        onChange={(e) => setDialogCostCenter(e.target.value)}
                        size="small"
                        required
                        error={Boolean(costCenterError)}
                        helperText={costCenterError ?? undefined}
                    />
                    <TextField
                        label={t('field.prName')}
                        value={dialogPrLabel}
                        onChange={(e) => setDialogPrLabel(e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.serviceFactor')}
                        type="number"
                        value={dialogServiceFactor}
                        onChange={(e) => setDialogServiceFactor(e.target.value)}
                        size="small"
                        inputProps={{ step: 0.1, min: 0 }}
                        error={Boolean(serviceFactorError)}
                        helperText={serviceFactorError ?? undefined}
                    />
                    <TextField
                        label={t('field.defaultAbt')}
                        type="number"
                        value={dialogDefaultAbt}
                        onChange={(e) => setDialogDefaultAbt(e.target.value)}
                        size="small"
                        inputProps={{ step: 1, min: 0 }}
                        error={Boolean(defaultAbtError)}
                        helperText={defaultAbtError ?? t('hint.fallbackAbt')}
                    />
                    <TextField
                        label={t('field.defaultMinutePrice')}
                        value={dialogDefaultMinutePrice}
                        onChange={(e) => setDialogDefaultMinutePrice(e.target.value)}
                        size="small"
                        inputProps={{ inputMode: 'decimal' }}
                        error={Boolean(defaultMinutePriceError)}
                        helperText={defaultMinutePriceError ?? t('hint.fallbackMinutePrice')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox checked={dialogFlatFee} onChange={(e) => setDialogFlatFee(e.target.checked)} />
                        }
                        label={t('field.flatFee')}
                    />
                </Box>
            </ResponsiveDialog>

            {importVersionId != null && (
                <BillingConfigImportDialog
                    open={showImportDialog}
                    onClose={() => setShowImportDialog(false)}
                    versionId={importVersionId}
                    bucketName={buckets.find((b) => b.id === selectedBucketId)?.name}
                />
            )}

            {showCompleteMissingDialog && selectedBucketId != null && (
                <CompleteMissingConfigsDialog
                    open={showCompleteMissingDialog}
                    onClose={() => setShowCompleteMissingDialog(false)}
                    bucketId={selectedBucketId}
                    bucketName={buckets.find((b) => b.id === selectedBucketId)?.name ?? ''}
                    bucketSignatures={bucketSignatures}
                    seedVersionId={importVersionId}
                />
            )}
        </Box>
    );
};

export default BillingConfigGrid;
